import { LitElement, html, css } from '@hh/global.js'

class HHParallax extends LitElement {
  static properties = {
    isPlaying: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    isPlayingMobile: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    type: {
      type: String
    },
    mime: {
      type: String
    },
    src: {
      type: String
    },
    typeMobile: {
      type: String
    },
    mimeMobile: {
      type: String
    },
    srcMobile: {
      type: String
    }
  }

  constructor() {
    super()
    this.isPlaying = true
    this.isPlayingMobile = true
  }

  connectedCallback() {
    super.connectedCallback()
    this.classList.remove('hidden')
  }

  isMobile() {
    return !window.matchMedia('(min-width: 1000px)').matches
  }

  onClick(e) {
    if (this.isMobile()) {
      if (this.typeMobile == 'video') {
        const videoEl = this.renderRoot.querySelector('.product-featured-content__mobile').querySelector('video')
        const isPaused = videoEl.paused
        isPaused ? videoEl.play() : videoEl.pause()
        this.isPlayingMobile = isPaused
      }
    } else {
      if (this.type == 'video') {
        const videoEl = this.renderRoot.querySelector('.product-featured-content__desktop').querySelector('video')
        const isPaused = videoEl.paused
        isPaused ? videoEl.play() : videoEl.pause()
        this.isPlaying = isPaused
      }
    }
  }

  static styles = css`
  .product-featured-content-parallax {
    --sm-aspect-ratio: calc(9/16);
    height: calc(100vw/var(--sm-aspect-ratio));
    position: relative;
    width: 100%;
  }

  .product-featured-content-parallax-wrapper {
    clip: rect(0, auto, auto, 0);
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .product-featured-content-parallax-item {
    height: 100vh;
    position: fixed;
    top: 0;
    transform: translateZ(0);
    width: 100%;
    will-change: transform;
  }

  .product-featured-content {
    align-items: center;
    display: none;
    height: 100%;
    justify-content: center;
    position: relative;
    width: 100%;
    z-index: 1;
  }

  .product-featured-content__mobile {
    display: flex;
  }

  video {
    --sm-aspect-ratio: calc(9/16);
    --initial-height: 100vh;
    --base-height-coef: 2;
    --width: calc(var(--initial-height)*var(--sm-aspect-ratio));
    min-width: 100%;
    position: absolute;
    max-width: unset;
  }

  img {
    bottom: 0;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    --object-position: center center;
    object-fit: cover;
    object-position: var(--object-position);
    transition: opacity .15s linear;
    width: 100%;
  }

  :host .hh-banner-video {
    position: relative;
  }

  .hh-banner-video__controls-container {
    position: absolute;
    display: none;
    bottom: 19px;
    right: 19px;
    z-index: 5;
  }

  .hh-banner-video__controls-container__mobile {
    display: block;
  }

  .hh-banner-video__controls-btn {
    padding: 0;
    border: none;
    background: none;
    color: var(--theme-color-reverse);
    cursor: pointer;
  }

  .hh-banner-video__controls-btn[data-state="playing"] .hh-banner__play-icon,
  .hh-banner-video__controls-btn[data-state="paused"] .hh-banner__pause-icon {
    display: none
  }

  @media screen and (min-width: 1000px) {
    .product-featured-content-parallax {
      --container-aspect-ratio: calc(16/9);
      height: calc(100vw/var(--container-aspect-ratio));
    }

    .product-featured-content {
      display: flex;
    }

    .hh-banner-video__controls-container {
      display: block;
    }

    .product-featured-content__mobile,
    .hh-banner-video__controls-container__mobile {
      display: none;
    }

    video {
      --aspect-ratio: calc(16/9);
      --width: calc(var(--initial-height)*var(--aspect-ratio));
    }
  }
  `

  videoMediaTemplate() {
    if (this.type == 'video') {
      return html`<video
        playsinline='1'
        muted='muted'
        autoplay='autoplay'
        loop='loop'
        control='0'
        disablepictureinpicture='true'
        preload='metadata'
      >
        <source src='${this.src}' type='${this.mime}'>
      </video>`
    }
  }

  imageMediaTemplate() {
    if (this.type == 'image') {
      return html`<img  src='${this.src}' loading='lazy'>`
    }
  }

  videoMediaMobileTemplate() {
    if (this.typeMobile == 'video') {
      return html`<video
        playsinline='1'
        muted='muted'
        autoplay='autoplay'
        loop='loop'
        control='0'
        disablepictureinpicture='true'
        preload='metadata'
      >
        <source src='${this.srcMobile}' type='${this.mimeMobile}'>
      </video>`
    }
  }

  imageMediaImageTemplate() {
    if (this.typeMobile == 'image') {
      return html`<img  src='${this.srcMobile}' loading='lazy'>`
    }
  }

  controlsTemplate() {
    if (this.type == 'video') {
      return html`
        <div class="hh-banner-video">
          <div class="hh-banner-video__controls-container">
            <button
              type="button"
              class="hh-banner-video__controls-btn"
              aria-label="Toggle video"
              @click="${this.onClick}"
              data-state="${this.isPlaying ? 'playing' : 'paused'}"
              aria-pressed="${this.isPlaying ? 'true' : 'false'}"
            >
              <div class="hh-banner__pause-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="2" width="6" height="20" rx="1" stroke="currentColor" stroke-width="1.5"/>
                  <rect x="15" y="2" width="6" height="20" rx="1" stroke="currentColor" stroke-width="1.5"/>
                </svg>
              </div>
              <div class="hh-banner__play-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.55 12.7 6.17 20.13c-.92.5-2.04-.15-2.04-1.2V4.07a1.38 1.38 0 0 1 2.04-1.2l13.38 7.43c.94.52.94 1.88 0 2.4Z" stroke="currentColor" stroke-width="1.25"/>
                </svg>
              </div>
            </button>
          </div>
          <div class="hh-banner-video__controls-container hh-banner-video__controls-container__mobile">
            <button
              type="button"
              class="hh-banner-video__controls-btn"
              aria-label="Toggle video"
              @click="${this.onClick}"
              data-state="${this.isPlayingMobile ? 'playing' : 'paused'}"
              aria-pressed="${this.isPlayingMobile ? 'true' : 'false'}"
            >
              <div class="hh-banner__pause-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="2" width="6" height="20" rx="1" stroke="currentColor" stroke-width="1.5"/>
                  <rect x="15" y="2" width="6" height="20" rx="1" stroke="currentColor" stroke-width="1.5"/>
                </svg>
              </div>
              <div class="hh-banner__play-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.55 12.7 6.17 20.13c-.92.5-2.04-.15-2.04-1.2V4.07a1.38 1.38 0 0 1 2.04-1.2l13.38 7.43c.94.52.94 1.88 0 2.4Z" stroke="currentColor" stroke-width="1.25"/>
                </svg>
              </div>
            </button>
          </div>
        </div>`
    }
  }

  render() {
    return html`
    <div class='product-featured-content-parallax' @click='${this.onClick}'>
      <div class='product-featured-content-parallax-wrapper'>
        <div class='product-featured-content-parallax-item'>
          <div class='product-featured-content product-featured-content__desktop'>
              ${this.videoMediaTemplate()}
              ${this.imageMediaTemplate()}
          </div>
          <div class='product-featured-content product-featured-content__mobile'>
              ${this.videoMediaMobileTemplate()}
              ${this.imageMediaImageTemplate()}
          </div>
          <slot></slot>
        </div>
      </div>
    </div>
    ${this.controlsTemplate()}
    `
  }
}

customElements.define('hh-parallax', HHParallax)
